import type { EventHandler } from "./EventHandler";
import type { IEvent } from "./IEvent";
import { debugLog } from "./utils";

// https://github.com/KeesCBakker/Strongly-Typed-Events-for-TypeScript
class EventDispatcher<TArg = void> implements IEvent<TArg> {
	private handlers = new Set<EventHandler<TArg>>();

	constructor(
		private readonly name: string,
		private readonly verbose: boolean = false,
	) {}

	/* Owner-only API (not exposed via `asEvent`) */
	public dispatch(arg: TArg): void {
		if (this.verbose) {
			debugLog(this.name, this.handlers.size, arg);
		}

		for (const handler of this.handlers) {
			handler(arg);
		}
	}

	public asEvent(): IEvent<TArg> {
		return this;
	}

	public clear(): void {
		this.handlers.clear();
	}
	/* *** */

	/* Subscriber API (exposed via `asEvent`) */
	public get handlersCount(): number {
		return this.handlers.size;
	}

	public subscribe(handler: EventHandler<TArg>) {
		this.handlers.add(handler);

		return {
			unsubscribe: () => this.unsubscribe(handler),
		};
	}

	public unsubscribe(handler: EventHandler<TArg>): void {
		this.handlers.delete(handler);
	}

	public once(handler: EventHandler<TArg>): void {
		const wrapper: EventHandler<TArg> = (arg) => {
			this.handlers.delete(wrapper);
			handler(arg);
		};

		this.handlers.add(wrapper);
	}
	/* *** */
}

export { EventDispatcher, type EventHandler, type IEvent };
